import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import setup from '../assets/data/setup.json';
import { useActiveSection } from '../hooks/useActiveSection';

const drawerWidth = 300;

type NavItem = [string, string, 'section' | 'route'];

const landingNavItems: NavItem[] = [
  ['Problems', 'familiar', 'section'],
  ['Solutions', 'what-i-build', 'section'],
  ['AI Agents', 'ai', 'section'],
  ['Case Studies', 'case-studies', 'section'],
  ['Engineering', '/work', 'route'],
  ['Resume', '/resume', 'route'],
  ['Contact', 'contact', 'section'],
];

const workNavItems: NavItem[] = [
  ['Home', '/', 'route'],
  ['About', 'about', 'section'],
  ['Experience', 'experience', 'section'],
  ['Projects', 'case-studies', 'section'],
  ['Timeline', '/timeline', 'route'],
  ['Resume', '/resume', 'route'],
  ['Cover Letter', '/cover-letter', 'route'],
  ['Contact', 'contact', 'section'],
];

function Navigation({
  parentToChild,
  modeChange,
  variant = 'landing',
}: {
  parentToChild: { mode: string };
  modeChange: () => void;
  variant?: 'landing' | 'work';
}) {
  const { mode } = parentToChild;
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navItems = variant === 'landing' ? landingNavItems : workNavItems;
  const sectionIds = navItems.filter(([, , type]) => type === 'section').map(([, id]) => id);
  const activeSection = useActiveSection(sectionIds);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (section: string) => {
    document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  const goHome = () => {
    if (variant === 'landing') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
    setMobileOpen(false);
  };

  const linkClass = (id: string, isRoute = false) => {
    const isActive = !isRoute && activeSection === id;
    return `text-[13px] font-medium transition-colors px-3 py-1.5 rounded-md ${
      isActive
        ? 'text-ink bg-canvas-muted'
        : 'text-ink-muted hover:text-ink hover:bg-canvas-muted'
    }`;
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ pt: 2 }}>
      <p className="text-base font-semibold text-ink mb-2 px-5">{setup.profile.name}</p>
      <p className="text-sm text-ink-muted mb-4 px-5">
        {variant === 'landing' ? 'Custom software for growing businesses' : setup.profile.title}
      </p>
      <List>
        {navItems.map(([label, id, type]) => (
          <ListItem key={label} disablePadding>
            {type === 'route' ? (
              <ListItemButton component={RouterLink} to={id} sx={{ mx: 1, borderRadius: 1 }}>
                <ListItemText primary={label} />
              </ListItemButton>
            ) : (
              <ListItemButton onClick={() => scrollToSection(id)} sx={{ mx: 1, borderRadius: 1 }}>
                <ListItemText primary={label} />
              </ListItemButton>
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box className="site-header" sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        component="nav"
        id="navigation"
        elevation={0}
        sx={{
          position: 'fixed',
          zIndex: 1100,
          backdropFilter: scrolled ? 'blur(16px) saturate(180%)' : 'none',
          backgroundColor: scrolled ? 'var(--color-canvas)' : 'transparent',
          borderBottom: scrolled ? '1px solid var(--color-line)' : 'none',
          transition: 'all 0.25s ease',
          opacity: scrolled ? 0.98 : 1,
        }}
      >
        <Toolbar className="max-w-content mx-auto w-full px-4 sm:px-6 lg:px-8 !min-h-[60px]">
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={goHome}
              className="text-sm font-semibold tracking-[-0.02em] text-ink hidden sm:block"
            >
              {setup.profile.name.split(' ')[0]}
              <span className="text-ink-muted font-normal">.</span>
            </button>

            <IconButton
              aria-label="open drawer"
              onClick={handleDrawerToggle}
              sx={{ display: { sm: 'none' }, color: 'var(--color-ink)' }}
            >
              <MenuIcon />
            </IconButton>
          </div>

          <Box
            className="hidden sm:flex items-center gap-0.5 px-1.5 py-1 rounded-pill border border-line bg-surface/80"
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            {navItems.map(([label, id, type]) =>
              type === 'route' ? (
                <RouterLink key={label} to={id} className={linkClass(id, true)}>
                  {label}
                </RouterLink>
              ) : (
                <button key={label} type="button" onClick={() => scrollToSection(id)} className={linkClass(id)}>
                  {label}
                </button>
              )
            )}
          </Box>

          <IconButton onClick={modeChange} aria-label="Toggle theme" sx={{ color: 'var(--color-ink)', flexShrink: 0 }}>
            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
        </Toolbar>
      </AppBar>
      <Toolbar className="!min-h-[60px]" sx={{ visibility: 'hidden' }} aria-hidden />

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
}

export default Navigation;
